import {
  Badge,
  Body,
  ButtCon,
  Button,
  CloudImage,
  Heading,
  Modal,
  Stack,
} from "@biom3/react";

import { MODAL_HEIGHT, MODAL_WIDTH, appConfig } from "@/constants";
import { usePassportProvider } from "@/context";
import { usePoints } from "@/context/PointsProvider";
import { useUserBalance } from "@/hooks/useUserBalance";
import type { QuestState } from "@ui-kit/QuestList/types";
import { useCallback, useEffect, useMemo, useState } from "react";

export type AssetHoldingModalProps = {
  visible: boolean;
  onClose: () => void;
  quest: QuestState;
  // Optional custom messages
  successTitle?: string;
  successDescription?: string;
  failureTitle?: string;
  failureDescription?: string;
  onBuyClick?: () => void;
  onClaimPoints?: () => void;
};

export const AssetHoldingModal = ({
  visible,
  onClose,
  quest,
  successTitle,
  successDescription,
  failureTitle,
  failureDescription,
  onBuyClick,
  onClaimPoints,
}: AssetHoldingModalProps) => {
  const { getERC721Balance, getERC20Balance, getERC1155Balance } =
    useUserBalance();
  const { walletAddress } = usePassportProvider();
  const [isLoading, setIsLoading] = useState(true);
  const [balance, setBalance] = useState(BigInt(0));
  const [showCheckAgainButton, setShowCheckAgainButton] = useState(false);
  const [isErrorLoadingBalance, setIsErrorLoadingBalance] = useState(false);

  const { potentialPointsMap, isCalculating } = usePoints();
  const questPoints = quest.ruleId ? potentialPointsMap[quest.ruleId] || 0 : 0;

  const fetchBalance = useCallback(async () => {
    setIsLoading(true);

    if (!walletAddress) {
      setIsLoading(false);
      return;
    }
    if (!quest.assetHoldingType || !quest.assetHoldingToken) {
      setIsLoading(false);
      return;
    }

    try {
      switch (quest.assetHoldingType) {
        case "erc20": {
          const result = await getERC20Balance(
            walletAddress,
            quest?.assetHoldingToken?.collectionAddress,
          );
          setBalance(BigInt(result.formatted.split(".")[0]));
          break;
        }
        case "erc721": {
          const result = await getERC721Balance(
            walletAddress,
            quest?.assetHoldingToken?.collectionAddress,
          );
          setBalance(BigInt(result));
          break;
        }
        case "erc1155": {
          if (quest?.assetHoldingToken?.tokenId) {
            const result = await getERC1155Balance(
              walletAddress,
              quest?.assetHoldingToken?.collectionAddress,
              [Number.parseInt(quest?.assetHoldingToken?.tokenId || "0", 10)],
            );
            setBalance(BigInt(result[0] || "0"));
          }
          break;
        }
      }
    } catch (error) {
      setBalance(BigInt(0));
      setIsErrorLoadingBalance(true);
    }

    setIsLoading(false);
  }, [
    getERC721Balance,
    getERC20Balance,
    getERC1155Balance,
    quest,
    walletAddress,
  ]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  const meetsBalanceRequirement = useMemo(
    () => balance >= BigInt(quest?.assetHoldingToken?.minimumBalance || 0),
    [balance, quest?.assetHoldingToken?.minimumBalance],
  );

  const getSuccessMessage = useCallback(() => {
    let title = "";
    let description = "";

    switch (quest?.assetHoldingType) {
      case "erc1155":
        title = `Great! You have ${quest?.assetHoldingToken?.name}`;
        description = `${quest.caption} Make sure to hold until promotion ends, otherwise points will be reversed.`;
        break;
      case "erc721":
        if (quest?.assetHoldingToken?.pointsMultiplier) {
          title = `Great! You have x${balance} from ${quest?.assetHoldingToken?.name}`;
          description =
            questPoints >= quest.increase
              ? `You've reached the maximum points reward of ${quest.increase} points! ${quest.caption} Hold until promotion ends to keep your points.`
              : `You're earning ${questPoints} points! ${quest.caption} Hold until promotion ends to keep your points.`;
        } else {
          title = `Great! You have x${quest?.assetHoldingToken?.minimumBalance} from ${quest?.assetHoldingToken?.name}`;
          description = `${quest.caption} Make sure to hold until promotion ends, otherwise points will be reversed`;
        }
        break;
      case "erc20":
        if (quest?.assetHoldingToken?.pointsMultiplier) {
          title = `Great! You have ${balance} ${quest?.assetHoldingToken?.symbol || quest?.assetHoldingToken?.name}`;
          description =
            questPoints >= quest.increase
              ? `${quest.caption} You've reached the maximum points reward of ${quest.increase} points!`
              : `${quest.caption} You're earning ${questPoints} points! Hold until promotion ends to keep your points.`;
        } else {
          title = `Great! You have x${questPoints} ${quest?.assetHoldingToken?.symbol || quest?.assetHoldingToken?.name}`;
          description = `${quest.caption} Make sure to hold until promotion ends, otherwise points will be reversed`;
        }
        break;
      default:
        title = "Great! Requirements met";
        description = "Make sure to maintain this until promotion ends";
        break;
    }

    if (successTitle) {
      title = successTitle;
    }

    if (successDescription) {
      description = successDescription;
    }

    return { title, description };
  }, [quest, successTitle, successDescription, questPoints, balance]);

  const getFailureMessage = useCallback(() => {
    let title = "";
    let description = "";

    switch (quest?.assetHoldingType) {
      case "erc1155":
        title = `You don't have ${quest?.assetHoldingToken?.name}`;
        description = quest?.assetHoldingToken?.isEarlyAdopterReward
          ? "This NFT was from a previous early-game quest and is no longer available."
          : `${quest.caption} To earn the points, you need to own it and hold it until promotion ends.`;
        break;
      case "erc721":
        title = `You don't own any items from ${quest?.assetHoldingToken?.name}`;
        description = quest?.assetHoldingToken?.isEarlyAdopterReward
          ? "These NFTs were from a previous early-game quest and are no longer available."
          : `${quest.caption} To earn the points, you need to own it and hold until promotion ends.`;
        break;
      case "erc20":
        title = `You don't have ${quest?.assetHoldingToken?.minimumBalance} ${quest?.assetHoldingToken?.symbol || quest?.assetHoldingToken?.name}`;
        description = `${quest.caption} To earn the points, you need to own it and hold it until promotion ends`;
        break;
      default:
        title = "Requirements not met yet";
        description = quest?.assetHoldingToken?.isEarlyAdopterReward
          ? "This was from a previous early-game quest and is no longer available."
          : `${quest.caption} You need to meet the requirements to earn points`;
        break;
    }

    if (failureTitle) {
      title = failureTitle;
    }

    if (failureDescription) {
      description = failureDescription;
    }

    return { title, description };
  }, [quest, failureTitle, failureDescription]);

  const { title, description } = useMemo(() => {
    if (isLoading || isCalculating) {
      return {
        title: "Checking your assets...",
        description: "Please wait while we verify your holdings",
      };
    }

    const { title, description } = meetsBalanceRequirement
      ? getSuccessMessage()
      : getFailureMessage();

    return {
      title,
      description,
    };
  }, [
    isLoading,
    isCalculating,
    meetsBalanceRequirement,
    getSuccessMessage,
    getFailureMessage,
  ]);

  const renderActionButtons = useCallback(() => {
    if (isLoading || isCalculating) {
      return (
        <Button size="large" variant="tertiary" disabled>
          Loading...
        </Button>
      );
    }

    if (isErrorLoadingBalance) {
      return (
        <>
          <Button size="large" variant="primary" onClick={fetchBalance}>
            Check Again
          </Button>
          <Button size="large" variant="tertiary" onClick={onClose}>
            Close
          </Button>
        </>
      );
    }

    if (
      !meetsBalanceRequirement &&
      quest.assetHoldingToken?.isEarlyAdopterReward
    ) {
      return (
        <Button size="large" variant="primary" onClick={onClose}>
          Close
        </Button>
      );
    }

    const getTertiaryButtonText = () => {
      // Early adopter reward that meets balance requirement
      if (
        meetsBalanceRequirement &&
        quest?.assetHoldingToken?.isEarlyAdopterReward
      ) {
        return "Close";
      }

      // Meets balance requirement and quest not completed yet
      if (meetsBalanceRequirement && !quest.completed) {
        return `Claim ${questPoints} point${questPoints === 1 ? "" : "s"}`;
      }

      if (quest.completed && meetsBalanceRequirement) {
        return "Close";
      }

      // Default case
      return "No, thanks";
    };

    return (
      <>
        {/* Only show Buy button if:
            1. User doesn't meet balance requirements
            2. It's not an early adopter reward (which can't be bought anymore)
            3. We have a buy action callback 
            4. We don't show the check again button
            */}
        {!meetsBalanceRequirement &&
          !quest?.assetHoldingToken?.isEarlyAdopterReward &&
          onBuyClick &&
          !showCheckAgainButton && (
            <Button
              size="large"
              variant="primary"
              onClick={() => {
                onBuyClick();
                setShowCheckAgainButton(true);
              }}
            >
              Buy It
            </Button>
          )}

        {showCheckAgainButton && (
          <Button size="large" variant="primary" onClick={fetchBalance}>
            Check Again
          </Button>
        )}

        <Button
          size="large"
          variant="tertiary"
          onClick={meetsBalanceRequirement ? onClaimPoints : onClose}
        >
          {getTertiaryButtonText()}
        </Button>
      </>
    );
  }, [
    isLoading,
    isCalculating,
    isErrorLoadingBalance,
    meetsBalanceRequirement,
    quest?.assetHoldingToken?.isEarlyAdopterReward,
    quest.completed,
    questPoints,
    onBuyClick,
    showCheckAgainButton,
    onClaimPoints,
    onClose,
    fetchBalance,
  ]);

  return (
    <Modal
      visible={visible}
      onCloseModal={onClose}
      showBgOverlay={false}
      outsideClicksClose={false}
    >
      <Modal.Content
        sx={{
          d: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: "base.borderRadius.x10",
          position: "relative",
          overflow: "hidden",
          background: "base.color.brand.2",
          backgroundImage: `url("${appConfig.ASSET_BASE_URL}/images/quests/follow-confirmation-background.svg")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "top",
          height: MODAL_HEIGHT,
          width: {
            default: "calc(100dvw - 40px)",
            small: MODAL_WIDTH,
          },
        }}
      >
        <Stack
          sx={{
            width: "100%",
            height: "100%",
            justifyContent: "space-between",
            py: "base.spacing.x10",
          }}
        >
          {/* Top content section */}
          <Stack
            sx={{
              width: "100%",
              alignItems: "center",
              gap: "base.spacing.x6",
            }}
          >
            {/* Early Adopter Badge if applicable */}
            {quest?.assetHoldingToken?.isEarlyAdopterReward && (
              <Badge
                variant="guidance"
                badgeContent="Early Adopter Bonus"
                sx={{
                  position: "absolute",
                  top: "base.spacing.x6",
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 1,
                }}
              />
            )}

            {/* Image and token ID */}
            <CloudImage
              sx={{
                width: "150px",
                height: "150px",
                mt: "base.spacing.x11",
                position: "relative",
                overflow: "hidden",
                objectFit: "cover",
                objectPosition: "center",
              }}
              imageUrl={quest?.assetHoldingToken?.image || null}
              relativeImageSizeInLayout="150px"
            />

            {/* Heading and description */}
            <Stack sx={{ width: "100%", alignItems: "center" }}>
              <Heading
                size="medium"
                weight="bold"
                sx={{
                  mx: "base.spacing.x12",
                  textAlign: "center",
                }}
              >
                {isErrorLoadingBalance ? "Something went wrong" : title}
              </Heading>
              <Body
                size="xSmall"
                sx={{
                  color: "base.color.text.body.secondary",
                  textAlign: "center",
                  pt: "base.spacing.x3",
                  mx: "base.spacing.x12",
                }}
              >
                {isErrorLoadingBalance
                  ? "Sorry looks like something went wrong checking your balance. Please close the modal and try again later. If the issue persists, please contact support."
                  : description}
              </Body>
            </Stack>
          </Stack>

          {/* Bottom buttons section */}
          <Stack
            sx={{
              width: "100%",
              gap: "10px",
              px: "base.spacing.x10",
            }}
          >
            {renderActionButtons()}
          </Stack>
        </Stack>

        <ButtCon
          icon="Close"
          onClick={onClose}
          sx={{
            position: "absolute",
            top: "base.spacing.x4",
            right: "base.spacing.x4",
          }}
          variant="tertiary"
        />
      </Modal.Content>
    </Modal>
  );
};
